import { useEffect, useState } from 'react';
import { useNotify, useRedirect } from 'react-admin';
import { Button } from '@mui/material';
import { Modal, Select, SelectProps } from 'antd';
import { clsx } from 'clsx';

import { axiosErrorToString, dateFormatter, moneyFormatter } from '@utils';
import {
  Credit,
  CreditInvoice,
  CreditInvoiceHistory,
  CreditInvoiceHistoryEntityType,
  CreditInvoiceStatusType,
} from '@types';
import { CalculateInvoiceResult, creditHttp } from '@network';
import { CreditInvoiceFormValues } from '@pages/credit/CreditEdit/CreditInvoices';
import cls from './credit-invoice-status.module.css';

const ApprovedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls.icon}
  >
    <path d="M20 6 9 17l-5-5"/>
  </svg>
);

const DeniedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls.icon}
  >
    <path d="M18 6 6 18"/>
    <path d="m6 6 12 12"/>
  </svg>
);

interface Props {
  currentStatus: CreditInvoiceStatusType;
  updateStatus: (status: CreditInvoiceStatusType) => void;
  submit: (creditId?: number) => Promise<void | CreditInvoice | null>
  credit: Credit;
  calculate: CalculateInvoiceResult | undefined;
  invoice: CreditInvoice;
  formValues: CreditInvoiceFormValues;
}

export const CreditInvoiceStatus = ({
  currentStatus,
  updateStatus,
  submit,
  credit,
  calculate,
  invoice,
  formValues,
}: Props) => {
  const redirect = useRedirect();
  const notify = useNotify();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [creditsList, setCreditsList] = useState<Credit[]>([]);
  const [selectedCreditId, setSelectedCreditId] = useState<number | null>(null);
  const [financeHistory, setFinanceHistory] = useState<CreditInvoiceHistory | null>(null);
  const [cxHistory, setCxHistory] = useState<CreditInvoiceHistory | null>(null);

  const isFinanceAnalysisSelected = formValues.isFinanceAnalysisApproved !== null;
  const isCxAnalysisSelected = formValues.isCxAnalysisApproved !== null;

  const selectCredit = (creditId: number) => {
    setSelectedCreditId(creditId);
  };

  const handleOk = () => {
    setIsModalOpen(false);

    if (selectedCreditId) {
      void submit(selectedCreditId);
    }
  };

  const fillHistory = (newInvoice: CreditInvoice) => {
    for (const history of (newInvoice?.history || [])) {
      if (history.entity === CreditInvoiceHistoryEntityType.FINANCE) {
        setFinanceHistory(history);
      } else if (history.entity === CreditInvoiceHistoryEntityType.CX) {
        setCxHistory(history);
      }
    }
  };

  const setFinanceAnalysisStatus = async (isApproved: boolean) => {
    if (isFinanceAnalysisSelected && isCxAnalysisSelected) {
      return;
    }

    try {
      const result = await creditHttp.setFinanceAnalysisStatus(invoice.id, {
        status: isApproved,
      });
      if (result) {
        formValues.isFinanceAnalysisApproved = isApproved;
      }

      const newInvoice = await submit(credit.id);
      if (newInvoice) {
        fillHistory(newInvoice);
        updateStatus(newInvoice.status);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const setCxAnalysisStatus = async (isApproved: boolean) => {
    if (isFinanceAnalysisSelected && isCxAnalysisSelected) {
      return;
    }

    try {
      const result = await creditHttp.setCxAnalysisStatus(invoice.id, {
        status: isApproved,
      });
      if (result) {
        formValues.isCxAnalysisApproved = isApproved;
      }

      const newInvoice = await submit(credit.id);
      if (newInvoice) {
        fillHistory(newInvoice);
        updateStatus(newInvoice.status);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const generateWithdrawalRecord = () => {
    redirect(`/finance-withdrawal/create?invoiceId=${invoice.id}`);
  };

  useEffect(() => {
    (async () => {
      try {
        const loadedCreditsList = await creditHttp.getCreditsByUser(credit.userId);
        setCreditsList(loadedCreditsList);

        const [firstCredit] = loadedCreditsList;
        setSelectedCreditId(firstCredit?.id || null);
      } catch (e) {
        notify(axiosErrorToString(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (invoice) {
      formValues.isFinanceAnalysisApproved = invoice.financeAnalysisStatus;
      formValues.isCxAnalysisApproved = invoice.cxAnalysisStatus;
    }
  }, [invoice]);

  const creditOptions: SelectProps['options'] = creditsList.map(creditOption => ({
    value: creditOption.id,
    label: `Credit #${creditOption.code} (${creditOption.statusEn}) Balance: ${moneyFormatter.format(creditOption.balance)}`,
  }));

  return (
    <div className={cls._}>
      <div className={cls.validator}>
        <div className={cls.validatorItem}>
          <div className={cls.validatorItemTitle}>Finance Analysis</div>
          <div className={cls.validatorControls}>
            <div
              className={clsx(
                cls.approve,
                formValues.isFinanceAnalysisApproved === true && cls.selected,
                isFinanceAnalysisSelected && isCxAnalysisSelected && cls.disabled,
              )}
              onClick={() => setFinanceAnalysisStatus(true)}
            >
              <ApprovedIcon />
            </div>
            <div
              className={clsx(
                cls.deny,
                formValues.isFinanceAnalysisApproved === false && cls.selected,
                isFinanceAnalysisSelected && isCxAnalysisSelected && cls.disabled,
              )}
              onClick={() => setFinanceAnalysisStatus(false)}
            >
              <DeniedIcon />
            </div>
          </div>
          <div className={cls.validatorItemComment}>
            {financeHistory && (
              <span>{`Last Update by ${financeHistory.adminName} | ${dateFormatter.toDateTime(financeHistory.createdAt)}`}</span>
            )}
          </div>
        </div>
        <div className={cls.validatorItem}>
          <div className={cls.validatorItemTitle}>CX Analysis</div>
          <div className={cls.validatorControls}>
            <div
              className={clsx(
                cls.approve,
                formValues.isCxAnalysisApproved === true && cls.selected,
                isFinanceAnalysisSelected && isCxAnalysisSelected && cls.disabled,
              )}
              onClick={() => setCxAnalysisStatus(true)}
            >
              <ApprovedIcon />
            </div>
            <div
              className={clsx(
                cls.deny,
                formValues.isCxAnalysisApproved === false && cls.selected,
                isFinanceAnalysisSelected && isCxAnalysisSelected && cls.disabled,
              )}
              onClick={() => setCxAnalysisStatus(false)}
            >
              <DeniedIcon />
            </div>
          </div>
          <div className={cls.validatorItemComment}>
            {cxHistory && (
              <span>{`Last Update by ${cxHistory.adminName} | ${dateFormatter.toDateTime(cxHistory.createdAt)}`}</span>
            )}
          </div>
        </div>
      </div>
      <div className={cls.wrap}>
        <div className={cls.action}>
          <span>Status</span>
        </div>
        <div className={clsx(cls.label, cls[currentStatus])}>
          {currentStatus}
        </div>
      </div>
      <Button
        className={cls.submit}
        variant="contained"
        onClick={generateWithdrawalRecord}
        disabled={!formValues.isFinanceAnalysisApproved || !formValues.isCxAnalysisApproved}
      >
        Generate Withdrawal Record
      </Button>

      <Modal
        title="Confirm order"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        className={cls.modal}
        centered
      >
        <div className={cls.modalTitle}>
          Order total: {moneyFormatter.format(calculate?.finalAmount ?? 0)}
        </div>
        <Select
          value={selectedCreditId}
          onChange={(e) => selectCredit(e)}
          className={cls.modalSelect}
          showSearch
          placeholder="Add product"
          options={creditOptions}
        />
      </Modal>
    </div>
  );
};
